import { useTranslation } from '@mint-lib/i18n';
import { Delete, Edit } from '@mint-lib/icons';
import { Avatar, AvatarFallback, Button, Chip } from '@mint-lib/tw-components';

import { CustomMetricsQueryResult } from '../../api/types.js';
import { Tooltip } from '../../components/atoms/Tooltip.jsx';
import { useCustomMetricsTypesLabelsByName } from '../../constants/index.js';
import { useFormattedDate, useIsReadOnlyUser } from '../../hooks/index.js';
import { TableSkeletonRows } from './DynamicTableSkeleton.jsx';
import { PaginatedTable } from './PaginatedTable.jsx';
import {
  TableBody,
  TableBodyCell,
  TableHead,
  TableHeaderCell,
  TableRow,
} from './TableComponents.jsx';

type CustomMetricsTableProps = Readonly<{
  data: CustomMetricsQueryResult;
  isLoading: boolean;
  onMetricEdit: (metricId: string) => void;
  onMetricDelete: (metricId: string) => void;
}>;

export default function CustomMetricsTable({
  data,
  isLoading,
  onMetricEdit,
  onMetricDelete,
}: CustomMetricsTableProps) {
  const { t } = useTranslation('@myn/import-hub');
  const { getFormattedDate } = useFormattedDate();
  const isReadOnlyUser = useIsReadOnlyUser();

  const customMetricsTypesLabelsByName = useCustomMetricsTypesLabelsByName();

  const metrics = data?.items;

  return (
    <PaginatedTable
      data={data}
      isLoading={isLoading}
      tableContainerProps={{ className: 'shrink-[1000]' }}
      className="table-fixed"
    >
      <TableHead>
        <TableRow>
          <TableHeaderCell>{t('Metric name')}</TableHeaderCell>
          <TableHeaderCell>{t('Type')}</TableHeaderCell>
          <TableHeaderCell>{t('Last application file')}</TableHeaderCell>
          <TableHeaderCell>{t('Date of creation')}</TableHeaderCell>
          <TableHeaderCell skeletonVariant="avatar">
            {t('Created by')}
          </TableHeaderCell>
        </TableRow>
      </TableHead>
      <TableBody>
        {isLoading ? (
          <TableSkeletonRows cellsCount={5} rowsCount={data.itemsPerPage} />
        ) : (
          metrics.map(
            ({
              id,
              label,
              metricType,
              lastFile,
              createdDate,
              isUsed,
              fromUser,
            }) => (
              <TableRow key={id} className="group">
                <TableBodyCell>{label}</TableBodyCell>
                <TableBodyCell>
                  {metricType ? (
                    <Chip variant="outline" color="silver">
                      {customMetricsTypesLabelsByName[metricType]}
                    </Chip>
                  ) : null}
                </TableBodyCell>
                <TableBodyCell>{lastFile}</TableBodyCell>
                <TableBodyCell>
                  {getFormattedDate(createdDate) ? (
                    <span>{getFormattedDate(createdDate)}</span>
                  ) : null}
                </TableBodyCell>
                <TableBodyCell>
                  <div className="flex w-full items-center justify-between">
                    <Avatar size="small" color="secondary">
                      <AvatarFallback
                        firstName={fromUser.firstName}
                        lastName={fromUser.lastName}
                      />
                    </Avatar>
                    {!isReadOnlyUser && (
                      <div className="hidden justify-end group-hover:flex">
                        <Button
                          icon
                          variant="text"
                          size="small"
                          onClick={() => onMetricEdit(id)}
                          aria-label={t('Edit custom metric')}
                          data-dd-action-name="Edit custom metric"
                        >
                          <Edit />
                        </Button>
                        <Tooltip
                          asChild
                          description={
                            isUsed
                              ? t(
                                  `This custom metric cannot be deleted as it's used as a KPI`,
                                )
                              : t('Remove custom metric')
                          }
                        >
                          <div>
                            <Button
                              icon
                              variant="text"
                              size="small"
                              aria-label={t('Delete custom metric')}
                              data-dd-action-name="Delete custom metric"
                              onClick={() => onMetricDelete(id)}
                              disabled={isUsed}
                            >
                              <Delete />
                            </Button>
                          </div>
                        </Tooltip>
                      </div>
                    )}
                  </div>
                </TableBodyCell>
              </TableRow>
            ),
          )
        )}
      </TableBody>
    </PaginatedTable>
  );
}
